import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { Transaction } from '@sentry/types';

// Initialize Sentry
export const initSentry = () => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      integrations: [new BrowserTracing()],
      // integrations: [
      //   Sentry.browserTracingIntegration(),
      //   Sentry.replayIntegration({
      //     maskAllText: true,
      //     blockAllMedia: true,
      //   }),
      // ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of transactions

      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample 10% of sessions
      replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors

      // Enable PII data collection
      sendDefaultPii: true,

      environment: process.env.NODE_ENV,

      release: process.env.REACT_APP_VERSION || '1.0.0',

      sampleRate: 1.0, // Capture 100% of errors

      // Configure beforeSend to filter sensitive data
      beforeSend(event) {
        // Don't send events if user is not authenticated
        if (!event.user?.id) {
          return null;
        }

        // Remove sensitive data
        if (event.request?.cookies) {
          delete event.request.cookies;
        }

        return event;
      },
      autoSessionTracking: true,
    });
  }
};

// Create a custom error boundary component
export const ErrorBoundary = Sentry.ErrorBoundary;

// Create a custom performance monitoring component
export const Profiler = Sentry.Profiler;

// Helper function to capture exceptions with context
export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope(scope => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
};

// Helper function to capture messages with severity
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) => {
  Sentry.withScope(scope => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureMessage(message, { level });
  });
};

// Helper function to set user context
export const setUserContext = (user: { id: string; email?: string; username?: string; role?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    role: user.role,
  });
};

// Helper function to clear user context
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Helper function to track performance
export const startTransaction = (name: string, op: string): Transaction => {
  const transaction = Sentry.getCurrentHub().startTransaction({
    name,
    op,
  });
  return transaction;
};

// Helper function to add breadcrumb
export const addBreadcrumb = (category: string, message: string, level: Sentry.SeverityLevel = 'info', data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
  });
};
